import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  SimpleGrid,
  Card,
  CardBody,
  Image,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { FaFileAlt } from 'react-icons/fa';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const Forms = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isAuthenticated } = useAuth();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Carica tutti i form dal backend
  useEffect(() => {
    const loadForms = async () => {
      try {
        setLoading(true);
        setError(null);

        // Carica tutti i form
        const response = await axios.get('/api/forms');

        if (response.data.data && response.data.data.length > 0) {
          setForms(response.data.data);
        } else {
          setForms([]); // Assicurati che forms sia un array vuoto
        }
      } catch (err) {
        console.error('Error loading forms:', err);
        setError('Errore nel caricamento dei form');
      } finally {
        setLoading(false);
      }
    };

    loadForms();
  }, []);

  // Funzione per ottenere l'URL dell'immagine del form
  const getFormImageUrl = (form) => {
    if (form.image?.url) {
      return form.image.url.startsWith('http')
        ? form.image.url
        : `${process.env.REACT_APP_STRAPI_URL || ''}${form.image.url}`;
    }
    return null;
  };

  // Funzione per ottenere il titolo del form
  const getFormTitle = (form) => {
    return form.titolo || 'Form senza titolo';
  };

  // Funzione per ottenere la descrizione del form
  const getFormDescription = (form) => {
    return (
      form.descrizione || 'Compila questo form per condividere la tua opinione'
    );
  };

  // Funzione per ottenere lo stato del form
  const getFormStatus = (form) => {
    return form.stato === true ? 'Attivo' : 'Non Attivo';
  };

  // Funzione per ottenere il colore del badge basato sullo stato
  const getBadgeColorScheme = (form) => {
    return form.stato === true ? 'teal' : 'red';
  };

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={8}>
            <Spinner size="xl" color="teal.500" />
            <Text fontSize="lg" color="gray.600">
              Caricamento dei form...
            </Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box minH="100vh" bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            <Box>
              <AlertTitle>Errore!</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Box>
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {forms.length > 0 ? (
        <>
          {/* HERO SECTION - Solo se ci sono form */}
          <Box bg="teal.600" py={20}>
            <Container maxW="container.xl">
              <VStack spacing={6} textAlign="center" color="white">
                <Heading
                  as="h1"
                  fontSize={{ base: '4xl', md: '6xl' }}
                  fontWeight="bold"
                >
                  📝 Form Disponibili
                </Heading>
                <Text fontSize={{ base: 'lg', md: 'xl' }} maxW="2xl">
                  Scegli il form che vuoi compilare e condividi la tua opinione
                  con noi
                </Text>
              </VStack>
            </Container>
          </Box>

          {/* CARDS SECTION */}
          <Container maxW="container.xl" py={16}>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
              {forms.map((form) => (
                <Card
                  key={form.id}
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="xl"
                  overflow="hidden"
                  shadow="lg"
                  _hover={{
                    transform: 'translateY(-4px)',
                    shadow: 'xl',
                    transition: 'all 0.3s ease',
                  }}
                  transition="all 0.3s ease"
                >
                  {/* IMMAGINE DEL FORM */}
                  <Box
                    height="200px"
                    bg="gray.100"
                    position="relative"
                    overflow="hidden"
                  >
                    {getFormImageUrl(form) ? (
                      <Image
                        src={getFormImageUrl(form)}
                        alt={getFormTitle(form)}
                        objectFit="cover"
                        width="100%"
                        height="100%"
                      />
                    ) : (
                      <Box
                        width="100%"
                        height="100%"
                        bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <FaFileAlt size={48} color="white" />
                      </Box>
                    )}

                    {/* BADGE */}
                    <Badge
                      position="absolute"
                      top={4}
                      right={4}
                      colorScheme={getBadgeColorScheme(form)}
                      variant="solid"
                      borderRadius="full"
                      px={3}
                      py={1}
                    >
                      <Text fontSize="xs">{getFormStatus(form)}</Text>
                    </Badge>
                  </Box>

                  {/* CONTENUTO DELLA CARD */}
                  <CardBody p={6}>
                    <VStack spacing={4} align="stretch">
                      <Heading as="h3" size="md" color="#3b235a" noOfLines={2}>
                        {getFormTitle(form)}
                      </Heading>

                      <Text color="gray.600" fontSize="sm" noOfLines={3}>
                        {getFormDescription(form)}
                      </Text>

                      <Button
                        as={
                          form.stato === true && isAuthenticated
                            ? Link
                            : undefined
                        }
                        to={
                          form.stato === true && isAuthenticated
                            ? `/forms/${form.documentId || form.id}`
                            : undefined
                        }
                        colorScheme="teal"
                        size="md"
                        width="full"
                        rightIcon={<FaFileAlt />}
                        isDisabled={form.stato !== true || !isAuthenticated}
                        opacity={
                          form.stato === true && isAuthenticated ? 1 : 0.6
                        }
                        cursor={
                          form.stato === true && isAuthenticated
                            ? 'pointer'
                            : 'not-allowed'
                        }
                        _hover={
                          form.stato === true && isAuthenticated
                            ? {
                                transform: 'scale(1.02)',
                                transition: 'all 0.2s ease',
                              }
                            : {}
                        }
                      >
                        {!isAuthenticated
                          ? 'Accedi per Compilare'
                          : form.stato === true
                            ? 'Compila Form'
                            : 'Form Non Disponibile'}
                      </Button>
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          </Container>
        </>
      ) : (
        /* 404 PAGE - Solo se non ci sono form */
        <Container maxW="container.xl" py={16}>
          <VStack spacing={8} py={20} maxW="lg" mx="auto" textAlign="center">
            {/* 404 TITLE - INTEGRATED WITH BACKGROUND */}
            <VStack spacing={6}>
              <Text
                fontSize="6xl"
                fontWeight="bold"
                color="gray.400"
                opacity={0.8}
              >
                🃏 404
              </Text>
              <Text fontSize="2xl" color="gray.500" fontWeight="medium">
                Form Not Found
              </Text>

              {/* DESCRIPTION - SUBTLE */}
              <Text fontSize="lg" color="gray.400" lineHeight="1.6" maxW="md">
                Nessun form disponibile al momento.
                <br />
                Torna più tardi per trovare nuovi form da compilare!
              </Text>

              {/* GAME ELEMENTS - MINIMAL AND INTEGRATED */}
              <HStack spacing={6} justify="center" mt={6}>
                <Text fontSize="3xl" opacity={0.6}>
                  🎲
                </Text>
                <Text fontSize="3xl" opacity={0.6}>
                  🃏
                </Text>
                <Text fontSize="3xl" opacity={0.6}>
                  🎯
                </Text>
              </HStack>

              {/* SIMPLE BUTTON - INTEGRATED STYLE */}
              <Button
                as={Link}
                to="/"
                variant="ghost"
                size="lg"
                color="gray.500"
                _hover={{
                  color: 'gray.700',
                  bg: 'gray.100',
                  transform: 'translateY(-2px)',
                }}
                transition="all 0.2s ease"
                mt={8}
              >
                🏠 Torna alla Home
              </Button>
            </VStack>
          </VStack>
        </Container>
      )}
    </Box>
  );
};

export default Forms;
